import { useMediaQuery } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'

import { get } from '@js/common/api'

import DesktopView from '@f-fertighaus/applications/OfficeMap/partials/DesktopView'
import MapFilters from '@f-fertighaus/applications/OfficeMap/partials/MapFilters'
import MapFiltersBar from '@f-fertighaus/applications/OfficeMap/partials/MapFiltersBar'
import MapView from '@f-fertighaus/applications/OfficeMap/partials/MapView'
import MobileView from '@f-fertighaus/applications/OfficeMap/partials/MobileView'
import { DEFAULT_MAP_RANGES } from '@f-fertighaus/templates/OfficeMapPage/OfficeMapPage'

const availableFilters = [
  {
    name: 'office_location',
    label: 'Bürostandort',
    type: 'location',
    info:
      'Wir empfehlen Ihnen den Bauort für verbesserte Preisangaben einzutragen, da diese sich je nach Umgebung unterscheiden.'
  },
  {
    name: 'expansion_stages',
    label: 'Ausbaustufe',
    type: 'radio',
    default: 'all',
    values: [
      {
        id: 'all',
        name: 'alle'
      },
      {
        id: 3,
        name: 'Schlüsselfertig',
        description: 'Die sichere und komfortable Variante',
        tooltip:
          'Den Hausauf- und Innenausbau übernimmt der Anbieter, sodass das Haus direkt bezogen werden kann.'
      },
      {
        id: 1,
        name: 'Ausbauhaus',
        description: 'Sparen Sie bis zu 50% durch Eigenleistung',
        tooltip:
          'Das Haus ist aufgebaut und verfügt über eine Außenhülle mit gedämmten Wänden, Türen und Fenstern. Der Innenausbau erfolgt in Eigenleistung.'
      },
      {
        id: 2,
        name: 'Bausatzhaus',
        description: 'Das Haus zum Selberbauen - bis zu 70% Ersparnis',
        tooltip:
          'Der Anbieter liefert das Haus als Bausatz. Für den Auf- und Ausbau ist der Bauherr größtenteils selbst verantwortlich, wird aber bei Bedarf durch den Anbieter fachlich unterstützt.  '
      }
    ],
    info:
      'Zustand eines Hauses bei der Übergabe an den Bauherren. Sie hat daher Auswirkung auf den Hauspreis.'
  },
  {
    name: 'construction_methods',
    label: 'Bauweise',
    labelPlural: 'Bauweisen',
    type: 'checkbox',
    values: [
      {
        id: 1,
        name: 'Fertighaus',
        tooltip:
          'Bei einem Fertighaus werden die Bauteile des Hauses vorgefertigt und im Anschluss auf dem Grundstück aufgestellt.'
      },
      {
        id: 2,
        name: 'Massivhaus',
        tooltip:
          'Ein Massivhaus wird nach dem "Stein-auf-Stein-Prinzip" auf der Baustelle aufgebaut.'
      },
      {
        id: 3,
        name: 'Blockhaus'
      }
    ],
    info:
      'Die Bauweise beschreibt, in welcher Konstruktion und auf welche Weise das Eigenheim errichtet wird.'
  },
  {
    name: 'vendor_nid',
    label: 'Anbietername',
    labelPlural: 'Anbietername',
    type: 'checkbox',
    values: vendors,
    default: 'Anbieter auswählen'
  },
  {
    name: 'location_type',
    label: 'Standorttyp',
    labelPlural: 'Standorttyp',
    type: 'checkbox',
    values: [
      {
        id: 'house_vendor',
        name: 'Zentrale'
      },
      {
        id: 'sales_profile',
        name: 'Beratungsbüro'
      },
      {
        id: 'model_house',
        name: 'Musterhaus'
      }
    ]
  }
]

const OfficeMap = ({ initialParams }) => {
  const isMobileView = useMediaQuery('(max-width: 767px)')
  const [params, setParams] = useState(initialParams)
  const [locationParams, setLocationParams] = useState({})
  const [apiResponse, setApiResponse] = useState({})
  const [activeID, setActiveID] = useState(null)
  const desktopWrap = useRef(null)

  const [panelDisplayed, setPanelDisplayed] = useState(false)

  const displayPanel = ({ filter, offsetLeft, isActive }) => {
    if (!filter || filter === panelDisplayed.filter) {
      setPanelDisplayed(false)
    } else {
      setPanelDisplayed({
        filter,
        offsetLeft,
        isActive
      })
    }
  }

  const removeFilter = filter => {
    const paramsToSet = { ...params }

    if (paramsToSet[`${filter}__in`]) {
      paramsToSet[`${filter}__in`] = ''
    }

    if (paramsToSet[`${filter}__range`]) {
      paramsToSet[`${filter}__range`] = ''
    }

    if (filter === 'office_location') {
      setLocationParams({})
    }

    updateQuery(paramsToSet)
    displayPanel({})
  }

  const updateQuery = queryParams => {
    setParams(currentParams => {
      const newParams = { ...currentParams, ...queryParams }

      if (!queryParams['offset']) {
        delete newParams['offset']
      }

      Object.keys(newParams).map(key => {
        if (newParams[key] === '') {
          delete newParams[key]
        }
      })

      return newParams
    })

    if (queryParams.hasOwnProperty('offset') && queryParams['offset'] === 0) {
      applyQuery()
    }
  }

  const [showApplyQueryCTA, setShowApplyQueryCTA] = useState(false)

  const removeLocationPrefilledStatus = () => {
    setLocationParams({ ...locationParams, prefilled: undefined })
  }

  const shouldApplyUpdatedQuery = () => {
    if (params.offset || locationParams.prefilled) {
      applyQuery()
      if (locationParams.prefilled) {
        removeLocationPrefilledStatus()
      }
    } else {
      setShowApplyQueryCTA(true)
    }
  }
  useEffect(shouldApplyUpdatedQuery, [params])

  const attemptToPrefillLocation = () => {
    const isMapRangeSameAsDefault =
      params.lat__range === DEFAULT_MAP_RANGES.lat__range &&
      params.lon__range === DEFAULT_MAP_RANGES.lon__range
    if (isMapRangeSameAsDefault) {
      const userData = JSON.parse(localStorage.getItem('user'))
      if (
        userData?.project?.delivery_region?.label &&
        userData?.project?.delivery_region?.location
      ) {
        const { label, location } = userData.project.delivery_region
        setLocationParams({ label, location, prefilled: true })
      }
    }
  }

  useEffect(attemptToPrefillLocation, [])

  const applyQuery = () => {
    get('partner-locations', {
      ...params,
      limit: 20
    })
      .then(response => {
        if (!isMobileView) {
          desktopWrap.current.scrollTop = 0
        }
        setApiResponse(response)
        setActiveID(null)
      })
      .catch(() => {
        return
      })
  }

  const paramsToQueryString = paramsToParse => {
    if (!paramsToParse || Object.keys(paramsToParse) < 1) {
      return ''
    }

    let string = ''

    Object.keys(paramsToParse).map((param, index) => {
      if (paramsToParse[param]) {
        string += (index > 0 ? '&' : '') + param + '=' + paramsToParse[param]
      }
    })

    return `?${string}`
  }

  const cleanUpAfterVendorFetching = () => {
    setShowApplyQueryCTA(false)

    if (Object.keys(params).length > 0) {
      window.history.replaceState(
        null,
        document.title,
        Object.keys(params).length > 0
          ? paramsToQueryString(params)
          : window.location.pathname
      )
    }
  }

  useEffect(cleanUpAfterVendorFetching, [apiResponse])

  const officeMapWrap = useRef(null)
  const calculateHeight = () => {
    if (officeMapWrap.current)
      officeMapWrap.current.style.height = `${window.innerHeight - 56}px`
  }

  useEffect(() => {
    window.addEventListener('resize', calculateHeight)
    calculateHeight()

    return () => {
      window.removeEventListener('resize', calculateHeight)
    }
  }, [])

  const [displayingFilters, setDisplayingFilters] = useState(false)
  const [activeIDonMap, setActiveIDonMap] = useState(activeID)
  const [mapClicked, setMapClicked] = useState(false)

  const propsForPartials = {
    data: {
      apiResponse,
      params,
      locationParams,
      activeID,
      activeIDonMap,
      mapClicked,
      showApplyQueryCTA,
      displayingFilters
    },
    actions: {
      updateQuery,
      applyQuery,
      setActiveID,
      setActiveIDonMap,
      setMapClicked,
      setLocationParams,
      setDisplayingFilters
    },
    availableFilters
  }

  return (
    <div className='[ f-d-flr f-pos-rel ] OfficeMap-wrap' ref={officeMapWrap}>
      <div className='MapView-wrap'>
        <MapView {...propsForPartials} />
      </div>
      {isMobileView ? (
        <div className='[ f-pos-ab f-overflow f-!interactive ] MobileView-wrap'>
          <MobileView
            {...propsForPartials}
            displayPanel={displayPanel}
            availableFilters={availableFilters}
            panelDisplayed={panelDisplayed}
            removeFilter={removeFilter}
            updateQuery={updateQuery}
          />
        </div>
      ) : (
        <div className='[ f-pos-rel ] DesktopView-wrap'>
          <div
            className='DesktopView-scroll has-smooth-scrolling'
            ref={desktopWrap}>
            <div className='[ f-p-h-020 f-p-t-020 ]'>
              <span className='[ f-d-bl f-t-body f-t-bold f-p-b-010 ]'>
                Filter
              </span>
              <MapFiltersBar
                params={params}
                locationParams={locationParams}
                displayPanel={displayPanel}
                availableFilters={availableFilters}
                panelDisplayed={panelDisplayed}
              />
              <MapFilters
                params={params}
                locationParams={locationParams}
                setLocationParams={setLocationParams}
                displayPanel={displayPanel}
                availableFilters={availableFilters}
                panelDisplayed={panelDisplayed}
                removeFilter={removeFilter}
                updateQuery={updateQuery}
                applyQuery={applyQuery}
                apiResult={apiResponse.results || []}
              />
            </div>
            <DesktopView {...propsForPartials} desktopWrap={desktopWrap} />
          </div>
        </div>
      )}
    </div>
  )
}

export default OfficeMap
